import { useState } from "react";
import type { Category } from "../../../shared/types";
import { getToday } from "../../../shared/types/date-helpers";
import { ConfirmDialog } from "../../../ui/atoms/ConfirmDialog";
import { ChevronRight } from "../../../ui/icons";

interface TemplatePanelProps {
  readonly onAddTask: (
    title: string,
    category: Category,
    dueDate: string | null,
  ) => void;
  readonly onAddEvent: (
    title: string,
    category: Category,
    date: string,
    startTime: string,
    endTime: string,
  ) => void;
}

interface Template {
  readonly name: string;
  readonly tasks: ReadonlyArray<{ title: string; category: Category }>;
  readonly events: ReadonlyArray<{
    title: string;
    category: Category;
    startTime: string;
    endTime: string;
  }>;
}

const TEMPLATES: readonly Template[] = [
  {
    name: "Routine matinale spirituelle",
    tasks: [
      { title: "Méditation", category: "spiritual" },
      { title: "Lecture", category: "spiritual" },
      { title: "Prière", category: "spiritual" },
    ],
    events: [
      {
        title: "Routine spirituelle",
        category: "spiritual",
        startTime: "06:00",
        endTime: "07:00",
      },
    ],
  },
  {
    name: "Semaine pro type",
    tasks: [
      { title: "Planning hebdomadaire", category: "professional" },
      { title: "Traiter emails", category: "professional" },
      { title: "Travail en profondeur", category: "professional" },
      { title: "Préparation réunions", category: "professional" },
      { title: "Revue hebdomadaire", category: "professional" },
    ],
    events: [],
  },
  {
    name: "Weekend personnel",
    tasks: [
      { title: "Sport/activité physique", category: "personal" },
      { title: "Temps en famille", category: "personal" },
      { title: "Hobby/passion", category: "personal" },
    ],
    events: [],
  },
];

export function TemplatePanel({ onAddTask, onAddEvent }: TemplatePanelProps) {
  const [pendingTemplate, setPendingTemplate] = useState<Template | null>(null);

  function applyTemplate(template: Template) {
    for (const taskDef of template.tasks) {
      onAddTask(taskDef.title, taskDef.category, null);
    }

    const today = getToday();
    for (const eventDef of template.events) {
      onAddEvent(
        eventDef.title,
        eventDef.category,
        today,
        eventDef.startTime,
        eventDef.endTime,
      );
    }
  }

  return (
    <div className="flex flex-col">
      {TEMPLATES.map((template, index) => (
        <button
          key={template.name}
          type="button"
          onClick={() => setPendingTemplate(template)}
          className={`group relative px-4 py-3 text-left border-x-0 border-t border-b first:border-t last:border-b ${
            index === 0 ? "border-t-0" : ""
          } border-border/40 hover:border-accent/40 hover:bg-accent/3 transition-all`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="font-medium text-sm text-ink group-hover:text-accent group-hover:underline decoration-ink-muted/30 underline-offset-2 transition-all">
                {template.name}
              </div>
              <div className="text-xs text-ink-muted mt-0.5 flex items-center gap-2">
                <span>
                  {template.tasks.length} tâche{template.tasks.length > 1 ? "s" : ""}
                </span>
                {template.events.length > 0 && (
                  <>
                    <span className="text-ink-faint/30">·</span>
                    <span>{template.events.length} événement</span>
                  </>
                )}
              </div>
            </div>
            <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronRight className="w-4 h-4 text-accent" />
            </div>
          </div>
        </button>
      ))}

      <ConfirmDialog
        open={pendingTemplate !== null}
        title="Appliquer ce modèle ?"
        message={pendingTemplate
          ? `Cela va ajouter ${pendingTemplate.tasks.length} tâche${pendingTemplate.tasks.length > 1 ? "s" : ""}${pendingTemplate.events.length > 0 ? ` et ${pendingTemplate.events.length} événement${pendingTemplate.events.length > 1 ? "s" : ""}` : ""}.`
          : ""}
        confirmLabel="Appliquer"
        onConfirm={() => {
          if (pendingTemplate) applyTemplate(pendingTemplate);
          setPendingTemplate(null);
        }}
        onCancel={() => setPendingTemplate(null)}
      />
    </div>
  );
}
